import React, { useMemo } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { getOrgConfig } from '../../utils/orgResolution';
import providerLogo from '../../assets/images/logos/lcs_logo.png';
import { useEffect } from 'react';
import { useRmsState } from '../../context/RmsStateContext';
import { isPublicReferralFormClosed } from '../../constants/orgFeatures';

/**
 * ReferralInitiationStart
 * -----------------------
 * Landing page for the "Digital Front Door" / single point of access referral system.
 * 
 * Design Philosophy:
 *   Instead of presenting multiple pathway buttons (school vs guardian, CYP vs adult),
 *   this page now serves as a simple entry point. The pathway determination happens
 *   within the form itself through branching questions.
 * 
 * What this page does:
 *   1. Displays welcoming information about the referral service
 *   2. Pre-warms the schema cache for faster form loading
 *   3. Provides a single "Start Referral" button
 * 
 * The unified form (referralInitiationUnified) handles:
 *   - "Who is this referral for?" (myself vs someone else)
 *   - "Adult or CYP?" 
 *   - Relationship to subject (determines CYP 1.1 vs CYP 1.2 pathway)
 *   - All details collection
 */
export default function ReferralInitiationStart() {
  const { orgId } = useMemo(() => getOrgConfig(), []);
  if (isPublicReferralFormClosed(orgId)) {
    return <Navigate to="/rms/school-sign-in" replace />;
  }
  return <ReferralInitiationStartPage />;
}

function ReferralInitiationStartPage() {
  const navigate = useNavigate();
  const { orgId } = useMemo(() => getOrgConfig(), []);
  // Destructure stable useCallback reference to avoid re-running the prewarm
  // effect on every context version bump (see RmsStateContext versionTick).
  const { ensurePreSchema } = useRmsState();

  // Pre-warm schema cache for faster form loading
  useEffect(() => {
    (async () => {
      try {
        if (!orgId) return;
        // Prewarm: unified schema for the branching referral form
        await ensurePreSchema({ orgId, formKey: 'referralInitiationUnified', force: true });
      } catch (_) {
        // best-effort; don't block UI
      }
    })();
  }, [orgId, ensurePreSchema]);

  const handleStartReferral = () => {
    navigate(`/rms/referral/init?orgId=${encodeURIComponent(orgId)}`);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      {/* Compact centered card container - matches sign-in page design */}
      <div className="w-full max-w-md">
        {/* Logo - centered at top */}
        <div className="flex justify-center mb-10">
          <img src={providerLogo} alt="Provider logo" className="h-16 w-auto" />
        </div>

        {/* Card content */}
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            ADHD and Autism Referral Portal
          </h1>

          <p className="text-gray-600 mb-8 leading-relaxed">
            Welcome to the referral portal for ADHD and Autism. This service is for
            school representatives referring a child or young person for an
            assessment.
          </p>

          {/* Information box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8 text-left">
            <h3 className="text-sm font-semibold text-blue-900 mb-2">Before you start, you'll need:</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• The school you represent</li>
              <li>• Your contact details</li>
              <li>• The child or young person's name and date of birth</li>
              <li>• Their home address</li>
            </ul>
          </div>

          {/* Primary CTA button */}
          <button
            onClick={handleStartReferral}
            className="w-full py-3 px-4 rounded-lg font-semibold text-white bg-primary hover:bg-primary/90 shadow-md hover:shadow-lg transition-all"
          >
            Start Referral
          </button>
          
          {/* Existing account sign-in */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              Already made a referral before?{' '}
              <button
                onClick={() => navigate('/rms/sign-in')}
                className="text-primary hover:text-primary/80 underline font-medium"
              >
                Sign in to your account
              </button>
              {' '}to make new referrals and manage your existing referrals.
            </p>
          </div>

          {/* Footer partnership note */}
          <div className="mt-12 flex items-center justify-center gap-2 text-sm text-gray-600">
            <span>Made in partnership with</span>
            <img src="/td_logo_full_2025.png" alt="ThinkDivergent" className="h-6 w-auto opacity-90" />
          </div>
        </div>
      </div>
    </div>
  );
}
